import { useTranslation } from "../../i18n";
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  Title,
} from 'chart.js';
import API_BASE_URL from '../../config/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler, Title);

const movingAverage = (arr, windowSize = 7) => {
  return arr.map((_, i) => {
    const start = Math.max(0, i - Math.floor(windowSize / 2));
    const end = Math.min(arr.length, i + Math.ceil(windowSize / 2));
    const slice = arr.slice(start, end).filter(v => v != null && !isNaN(v));
    if (!slice.length) return arr[i];
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
};

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { labels: { font: { size: 10 }, boxWidth: 12 } },
  },
  scales: {
    x: { type: 'linear', ticks: { font: { size: 9 }, maxTicksLimit: 10 }, title: { display: true, text: '', font: { size: 9 } } },
    elev: { position: 'left', ticks: { font: { size: 9 } }, title: { display: true, text: '', font: { size: 9 } } },
    fc: { position: 'right', ticks: { font: { size: 9 } }, grid: { drawOnChartArea: false }, title: { display: true, text: '', font: { size: 9 } } },
  },
};

const CyclingElevationProfile = ({ fileId }) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const calculateElevationGain = (elevations) => {
    let gain = 0;
    for (let i = 1; i < elevations.length; i++) {
      if (elevations[i] > elevations[i - 1]) gain += elevations[i] - elevations[i - 1];
    }
    return gain.toFixed(2);
  };

  const calculateNormalizedPower = (wattsArray) => {
    if (!wattsArray?.length) return null;
    const rollingAverages = [];
    for (let i = 0; i <= wattsArray.length - 30; i++) {
      const slice = wattsArray.slice(i, i + 30);
      const avg = slice.reduce((sum, w) => sum + w, 0) / slice.length;
      rollingAverages.push(avg);
    }
    const fourthPowerAvg = rollingAverages.reduce((sum, r) => sum + Math.pow(r, 4), 0) / rollingAverages.length;
    return Math.round(Math.pow(fourthPowerAvg, 0.25));
  };

  useEffect(() => {
    const fetchCyclingData = async () => {
      if (!fileId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        const records = res.data.records ?? [];
        const thresholdHR = res.data.zones_target?.threshold_heart_rate ?? 170;
        const weight = res.data.fitFileDetails?.user_profile?.weight ?? 70;

        const validRecords = records.filter(r =>
          typeof r.distance !== 'undefined' &&
          typeof r.altitude !== 'undefined' &&
          !isNaN(parseFloat(r.distance)) &&
          !isNaN(parseFloat(r.altitude))
        );

        const processed = validRecords.map((r) => {
          const watts = r.power ?? (r.enhanced_speed ? parseFloat((weight * r.enhanced_speed * 0.1).toFixed(2)) : null);
          const IF = r.heart_rate && thresholdHR ? parseFloat((r.heart_rate / thresholdHR).toFixed(2)) : null;
          return {
            distance: parseFloat((r.distance / 1000).toFixed(2)),
            elevation: parseFloat(r.altitude.toFixed(2)),
            heartRate: r.heart_rate ?? null,
            watts,
            IF,
          };
        });

        setChartData(processed);

        const elevations = processed.map(d => d.elevation);
        const ifValues = processed.filter(d => d.IF);
        const avgIF = ifValues.length
          ? (ifValues.reduce((sum, d) => sum + d.IF, 0) / ifValues.length).toFixed(2)
          : null;

        setSummary({
          totalDistance: (res.data.session?.total_distance / 1000).toFixed(2),
          maxElevation: Math.max(...elevations).toFixed(2),
          minElevation: Math.min(...elevations).toFixed(2),
          elevationGain: calculateElevationGain(elevations),
          normalizedPower: calculateNormalizedPower(processed.map(d => d.watts).filter(Boolean)),
          intensityFactor: avgIF,
        });
      } catch (err) {
        setError('Error');
      } finally {
        setLoading(false);
      }
    };

    fetchCyclingData();
  }, [fileId]);

  const chartJsData = useMemo(() => {
    const xStart = chartData[0]?.distance ?? 0;
    const xEnd = chartData[chartData.length - 1]?.distance ?? 0;
    const maxElev = parseFloat(summary.maxElevation);
    const minElev = parseFloat(summary.minElevation);
    const smoothElevation = movingAverage(chartData.map(d => d.elevation));
    const smoothHeartRate = movingAverage(chartData.map(d => d.heartRate));
    const smoothWatts = movingAverage(chartData.map(d => d.watts));
    return {
    datasets: [
      {
        label: t('elevationProfile.seriesElevation'),
        data: chartData.map((d, i) => ({ x: d.distance, y: smoothElevation[i] })),
        borderColor: 'rgba(0,128,0,0.9)',
        backgroundColor: 'rgba(0,128,0,0.12)',
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        yAxisID: 'elev',
        borderWidth: 2.5,
        order: 1,
      },
      {
        label: `${t('elevationProfile.altMax')}: ${summary.maxElevation} m`,
        data: [{ x: xStart, y: maxElev }, { x: xEnd, y: maxElev }],
        borderColor: 'rgba(220,38,38,0.7)',
        backgroundColor: 'transparent',
        yAxisID: 'elev',
        fill: false,
        pointRadius: 0,
        borderWidth: 1.2,
        borderDash: [6, 3],
        order: 5,
      },
      {
        label: `${t('elevationProfile.altMin')}: ${summary.minElevation} m`,
        data: [{ x: xStart, y: minElev }, { x: xEnd, y: minElev }],
        borderColor: 'rgba(59,130,246,0.7)',
        backgroundColor: 'transparent',
        yAxisID: 'elev',
        fill: false,
        pointRadius: 0,
        borderWidth: 1.2,
        borderDash: [6, 3],
        order: 6,
      },
      {
        label: t('elevationProfile.seriesHr'),
        data: chartData.map((d, i) => ({ x: d.distance, y: smoothHeartRate[i] })),
        borderColor: 'rgba(239,68,68,0.9)',
        backgroundColor: 'rgba(239,68,68,0.05)',
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        yAxisID: 'fc',
        borderWidth: 1.5,
        order: 2,
      },
      {
        label: t('elevationProfile.seriesPowerCycling'),
        data: chartData.map((d, i) => ({ x: d.distance, y: smoothWatts[i] })),
        borderColor: 'rgba(147,51,234,0.9)',
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        yAxisID: 'fc',
        borderWidth: 1.2,
        borderDash: [4, 2],
        order: 3,
      },
    ],
  };
  }, [chartData, t, summary]);

  const chartJsOptions = useMemo(() => {
    const xMax = chartData.length ? chartData[chartData.length - 1].distance : undefined;
    return {
      ...CHART_OPTIONS,
      scales: {
        ...CHART_OPTIONS.scales,
        x: { ...CHART_OPTIONS.scales.x, max: xMax, title: { ...CHART_OPTIONS.scales.x.title, text: t('elevationProfile.axisDistance') } },
        elev: { ...CHART_OPTIONS.scales.elev, title: { ...CHART_OPTIONS.scales.elev.title, text: t('elevationProfile.axisElevation') } },
        fc: { ...CHART_OPTIONS.scales.fc, title: { ...CHART_OPTIONS.scales.fc.title, text: t('elevationProfile.axisPowerBpm') } },
      },
    };
  }, [chartData, t]);

  if (!fileId) return <div>No se recibio fileId</div>;
  if (loading) return <div>{t("elevationProfile.loadingCycling")}</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!chartData.length) return null;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#193255', marginBottom: 4 }}>
        {t("elevationProfile.titleCycling")}
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
        {t('elevationProfile.totalDistance')} {summary.totalDistance} km · {t('elevationProfile.maxElevation')} {summary.maxElevation} m · {t('elevationProfile.minElevation')} {summary.minElevation} m · {t('elevationProfile.positiveSlope')} {summary.elevationGain} m
        {summary.normalizedPower ? ` · NP: ${summary.normalizedPower} W` : ''}
        {summary.intensityFactor ? ` · IF: ${summary.intensityFactor}` : ''}
      </div>
      <div style={{ width: '100%', height: '260px' }}>
        <Line data={chartJsData} options={chartJsOptions} />
      </div>
    </div>
  );
};

export default CyclingElevationProfile;
